<template>
  <div class="content">
    <h2 class="content-heading">
      <i class="fa fa-angle-right text-muted mr-1"></i> {{$t('accountProfile.results.profile')}}
    </h2>
    <div class="block block-rounded" :class="{ 'block-mode-loading': isLoading }">
      <div class="block-content block-content-full">
        <div class="row text-center">
          <div class="col-md-4 py-3">
            <div v-text="accountProfile.account" class="font-size-h1 font-w300 text-black mb-1 text-uppercase"></div>
            <span class="font-size-sm font-w700 text-uppercase text-muted" >{{$t('accountProfile.results.account')}}</span>
          </div>
          <div class="col-md-4 py-3">
            <div v-text="accountProfile.token" class="font-size-h1 font-w300 text-black mb-1"></div>
            <span class="font-size-sm font-w700 text-uppercase text-muted" >{{$t('accountProfile.results.token')}}</span>
          </div>
          <div class="col-md-4 py-3">
            <div v-text="accountProfile.total_tokens_received" class="font-size-h1 font-w300 text-black mb-1"></div>
            <span class="font-size-sm font-w700 text-uppercase text-muted" >{{$t('accountProfile.results.totalReceived')}}</span>
          </div>
        </div>
      </div>
    </div>
    <h2 class="content-heading">
      <i class="fa fa-angle-right text-muted mr-1"></i> {{$t('accountProfile.results.score')}}
    </h2>
    <div class="block block-rounded" :class="{ 'block-mode-loading': isLoading }">
      <div class="block-content block-content-full">
        <div class="row">
          <div class="col-md-4 d-md-flex align-items-md-center">
            <div class="p-md-2 p-lg-3">
              <div v-text="accountProfile.score + '%'" class="display-4 text-success font-w700" :class="{'text-success': accountProfile.score >= 70, 'text-danger': accountProfile.score < 70}"></div>
              <div class="font-size-lg font-w700">{{$t('accountProfile.results.profileScore')}}</div>
              <div v-if="accountProfile.score >= 70" class="py-3 d-flex align-items-center">
                <div class="bg-success-lighter px-2 py-1 rounded mr-3">
                  <i class="fa fa-fw fa-thumbs-up text-success"></i>
                </div>
                <p class="mb-0">
                  {{$t('accountProfile.results.yourScoreIs')}} <span class="font-w600 text-success">{{$t('accountProfile.results.goodScore')}}!</span>
                </p>
              </div>
              <div v-else class="py-3 d-flex align-items-center">
                <div class="bg-danger-lighter px-2 py-1 rounded mr-3">
                  <i class="fa fa-fw fa-thumbs-down text-danger"></i>
                </div>
                <p class="mb-0">
                  {{$t('accountProfile.results.yourScoreIs')}} <span class="font-w600 text-danger">{{$t('accountProfile.results.badScore')}}!</span>
                </p>
              </div>
            </div>
          </div>
          <div class="col-md-8 d-flex flex-row-reverse">
            <div class="chart">
              <pie-chart :chart-data="pieChartData" :options="options"></pie-chart>
            </div>
          </div>
        </div>
      </div>
    </div>
    <h2 class="content-heading">
      <i class="fa fa-angle-right text-muted mr-1"></i> {{$t('accountProfile.results.categoryDetails')}}
    </h2>
    <category-details :categories="accountProfile.categories"/>
    <h2 class="content-heading">
      <i class="fa fa-angle-right text-muted mr-1"></i> {{$t('accountProfile.results.currentResources')}}
    </h2>
    <div class="block block-rounded">
      <div class="block-content block-content-full">
        <div class="row text-center">
          <div class="col-md-4 py-3">
            <div v-text="total_balance" class="font-size-h1 font-w300 text-black mb-1"></div>
            <span class="font-size-sm font-w700 text-uppercase text-muted">{{$t('accountProfile.results.total')}}</span>
          </div>
          <div class="col-md-4 py-3">
            <div v-text="core_liquid_balance" class="font-size-h1 font-w300 text-success mb-1"></div>
            <span class="font-size-sm font-w700 text-uppercase text-muted">{{$t('accountProfile.results.available')}}</span>
          </div>
          <div class="col-md-4 py-3">
            <div v-text="self_delegated_bandwidth" class="font-size-h1 font-w300 text-warning mb-1"></div>
            <span class="font-size-sm font-w700 text-uppercase text-muted">{{$t('accountProfile.results.selfStaked')}}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'
import PieChart from './PieChart'
import TransferDetails from '@/components/common/TransferDetails'
import CategoryDetails from './CategoryDetails'
import { utils } from 'eos-detective'
/* eslint-disable */
export default {
  props: ['account'],
  components: {
    CategoryDetails,
    PieChart,
    TransferDetails
  },
  name: 'AccountProfileResults',
  computed: {
    isLoading () {
      return this.$store.state.options.isLoading
    },
    search: {
      // getter
      get () {
        return this.$store.state.filter.search
      },
      // setter
      set (value) {
        this.$store.commit('setSearch', value)
      }
    },
    accountProfile () {
      return this.$store.state.profile.accountProfileResults
    },
    userApiToken () {
      return this.$store.state.auth.profile.userApiToken
    },
    userTier () {
      return this.$store.state.auth.profile.userTier
    },
    total_balance () {
      return this.core_liquid_balance + this.self_delegated_bandwidth
    },
    self_delegated_bandwidth () {
      return this.cpu_weight + this.net_weight
    },
    net_weight () {
      if (this.accountChainInfo.self_delegated_bandwidth) {
        const net_weight = this.accountChainInfo.self_delegated_bandwidth.net_weight
        if (net_weight) {
          const [amount] = utils.splitQuantity(net_weight)
          return amount
        }
      }
      return 0
    },
    cpu_weight () {
      if (this.accountChainInfo.self_delegated_bandwidth) {
        const cpu_weight = this.accountChainInfo.self_delegated_bandwidth.cpu_weight
        if (cpu_weight) {
          const [amount] = utils.splitQuantity(cpu_weight)
          return amount
        }
      }
      return 0
    },
    core_liquid_balance () {
      const core_liquid_balance = this.accountChainInfo.core_liquid_balance

      if (core_liquid_balance) {
        const [amount] = utils.splitQuantity(core_liquid_balance)
        return amount
      }
      return 0
    },
    accountChainInfo () {
      return this.$store.state.profile.accountChainInfo
    },
    pieChartData () {
      let dataset = []
      let labels = []
      let categories = []
      categories['genesis'] = '#82b54b'
      categories['bpay'] = '#82b54b'
      categories['vpay'] = '#82b54b'
      categories['cex'] = '#82b54b'
      categories['games'] = '#82b54b'
      categories['account'] = '#82b54b'
      categories['namebid'] = '#82b54b'
      categories['candy'] = '#82b54b'
      categories['resources'] = '#82b54b'
      categories['gambling'] = '#ffb119'
      categories['dex'] = '#FF8329'
      categories['blacklist'] = '#e02718'
      categories['social'] = '#99B2C1'
      categories['stake'] = '#99B2C1'
      categories['eosio'] = '#99B2C1'
      categories['ram'] = '#99B2C1'
      categories['names'] = '#99B2C1'
      categories['uncategorized'] = '#aac1cf'
      let bgColors = []
      for (var key in categories) {
        if (this.accountProfile.categories.hasOwnProperty(key)) {
          labels.push(key)
          dataset.push(this.accountProfile.categories[key].percentage * 100)
          bgColors.push(categories[key])
        }
      }
      return { labels: labels, datasets: [ { data: dataset, backgroundColor: bgColors } ] }
    }
  },
  data () {
    return {
      options: {
        legend: {
          display: true,
          position: 'bottom'
        }
      }
    }
  },
  created () {
    if (!this.search) {
      this.search = this.account
    }
    if (this.userApiToken) {
      this.loadAccountProfile('')
    } else this.setLoading(true)
  },
  methods: {
    ...mapActions([
      'loadAccountProfile'
    ]),
    ...mapMutations([
      'setLoading'
    ]),
  },
  watch: {
    userApiToken () {
      if (this.userTier >= 3) {
        this.loadAccountProfile('')
      } else {
        this.$router.replace({ name: 'dashboard' })
        this.setLoading(false)
      }
    }
  },
  beforeDestroy () {
    this.$store.commit('resetAccountProfile')
  }
}
</script>

<style scoped>
  .chart {
    max-width: 700px;
  }
</style>
